import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import axios from 'axios';
import classNames from 'classnames';
import {RadioGroup} from '@headlessui/react';

import {WebsiteContext} from '../../index';
import ContentTransition from '../../components/modal-content-transition';
import getSafeReturnUrl from '../account-block/account-return-url';
import AuthFormShell from '../account-block/auth-form-shell';
import RegistrationForm from '../account-block/registration-form';
import {
    buildRegistrationChoiceUrl,
    ensureRegistrationLinkKeys,
    normalizeRegistrationLinkKey
} from './registration-choice-utils';

export const getRegistrationChoiceKey = (registration, index) => (
    String(registration?.id || registration?.form?.uuid || registration?.form?._id || index)
);

export {buildRegistrationChoiceUrl, ensureRegistrationLinkKeys, normalizeRegistrationLinkKey};

export const getRequestedRegistrationChoiceKey = (search, choices) => {
    const requestedKey = normalizeRegistrationLinkKey(new URLSearchParams(search || '').get('type'));
    if (!requestedKey) return null;

    const matches = choices.filter(choice => (
        normalizeRegistrationLinkKey(choice.linkKey) === requestedKey && choice.linkKey
    ));
    return matches.length === 1 ? matches[0].choiceKey : null;
};

export const getRegisteredVisitorReturnUrl = returnUrl => (
    returnUrl === '/' ? '/my-account/profile' : returnUrl
);

export const getRegistrationNodeContext = (node, pathname = '/') => {
    if (node?.uuid || node?._id) {
        return {
            uuid: node.uuid || node._id,
            ...(node.path ? {path: node.path} : {}),
            ...(node.uri ? {uri: node.uri} : {})
        };
    }

    if (node?.path || node?.uri) {
        return {
            ...(node.path ? {path: node.path} : {}),
            ...(node.uri ? {uri: node.uri} : {})
        };
    }

    return {path: pathname || '/'};
};

const normalizeRoute = value => String(value || '').split('?')[0].replace(/\/+$/, '').toLowerCase();

export const isRegisterSystemPage = node => (
    String(node?.nodeType || '').toLowerCase() === 'systempage' && (
        String(node?.systemPageKey || '').toLowerCase() === 'register' ||
        normalizeRoute(node?.action?.value) === '/my-account/register'
    )
);

export const seedDefaultRegistrationForm = (registrations, registrationForm) => {
    if (!registrationForm) return registrations;

    let seeded = false;
    return registrations.map(registration => {
        const hasForm = registration?.form?.uuid || registration?.form?._id;
        if (seeded || hasForm) return registration;

        seeded = true;
        return {
            ...registration,
            form: {
                ...(registration.form || {}),
                uuid: registrationForm
            }
        };
    });
};

export const registrationFormUnavailableReason = form => {
    if (!form?.uuid && !form?._id) return 'This registration option is currently unavailable.';
    if (form.isActive === false) return 'This registration option is not currently active.';
    if (form.rules && (form.rules.customer !== true || form.rules.registerCustomer !== true)) {
        return 'This form is not configured for account registration.';
    }
    return null;
};

export const getBrowserRegistrationReturnUrl = (browserState, fixedRegisterPage) => {
    if (!browserState) return '/';

    const params = new URLSearchParams(browserState.search || '');
    if (params.has('returnUrl')) {
        return getSafeReturnUrl({search: browserState.search, state: null}, browserState.origin);
    }
    if (fixedRegisterPage) return '/';

    return `${browserState.pathname || '/'}${browserState.search || ''}${browserState.hash || ''}`;
};

export default function RegistrationBlock(props) {
    const websiteContext = useContext(WebsiteContext);
    const configuredRegistrations = Array.isArray(props.values?.registrations) ? props.values.registrations : [];
    const registrations = useMemo(() => ensureRegistrationLinkKeys(seedDefaultRegistrationForm(
        configuredRegistrations,
        websiteContext?.website?.store?.registrationForm
    )), [configuredRegistrations, websiteContext?.website?.store?.registrationForm]);
    const choices = useMemo(() => registrations.map((registration, index) => ({
        ...registration,
        choiceKey: getRegistrationChoiceKey(registration, index)
    })), [registrations]);
    const [activeChoiceKey, setActiveChoiceKey] = useState(() => choices[0]?.choiceKey);
    const [browserState, setBrowserState] = useState(null);
    const [profileCheck, setProfileCheck] = useState('idle');
    const initializedFromQuery = useRef(false);
    const isBuilderPreview = websiteContext?.builder === true || websiteContext?.picker === true;
    const fixedRegisterPage = isRegisterSystemPage(websiteContext?.node);

    useEffect(() => {
        setBrowserState({
            origin: window.location.origin,
            pathname: window.location.pathname,
            search: window.location.search,
            hash: window.location.hash
        });
    }, []);

    const requestedChoiceKey = getRequestedRegistrationChoiceKey(browserState?.search, choices);
    useEffect(() => {
        if (browserState && !initializedFromQuery.current) {
            initializedFromQuery.current = true;
            if (requestedChoiceKey) {
                setActiveChoiceKey(requestedChoiceKey);
                return;
            }
        }

        if (!choices.some(choice => choice.choiceKey === activeChoiceKey)) {
            setActiveChoiceKey(choices[0]?.choiceKey);
        }
    }, [browserState, choices, activeChoiceKey, requestedChoiceKey]);

    useEffect(() => {
        if (!fixedRegisterPage || isBuilderPreview) return undefined;

        let active = true;
        setProfileCheck('checking');
        axios.get('/api/v2/publicusers/profile')
            .then(response => {
                if (active) {
                    setProfileCheck(response.data?.customer?._links?.edit ? 'authenticated' : 'guest');
                }
            })
            .catch(() => {
                if (active) setProfileCheck('guest');
            });

        return () => {
            active = false;
        };
    }, [fixedRegisterPage, isBuilderPreview]);

    const activeChoice = choices.find(choice => choice.choiceKey === activeChoiceKey) || choices[0];
    const returnUrl = getBrowserRegistrationReturnUrl(browserState, fixedRegisterPage);
    const registeredVisitorReturnUrl = getRegisteredVisitorReturnUrl(returnUrl);
    const isAuthenticated = websiteContext?.customerSession?.isAuthenticated === true ||
        profileCheck === 'authenticated';
    const node = getRegistrationNodeContext(websiteContext?.node, browserState?.pathname);

    useEffect(() => {
        if (!fixedRegisterPage || !isAuthenticated || isBuilderPreview || !browserState) return;

        const destination = `${browserState.origin}${registeredVisitorReturnUrl}`;
        if (props.onRegisteredRedirect) {
            props.onRegisteredRedirect(destination);
            return;
        }

        window.location.replace(destination);
    }, [browserState, fixedRegisterPage, isAuthenticated, isBuilderPreview, props.onRegisteredRedirect,
        registeredVisitorReturnUrl]);

    if (fixedRegisterPage && isAuthenticated && !isBuilderPreview) {
        return <p role="status" className="section text-center">Taking you to your account…</p>;
    }

    if (!fixedRegisterPage && isAuthenticated && !isBuilderPreview) {
        return (
            <div className="section text-center">
                <p>You are already signed in.</p>
                <a className="button justify-center mt-4" href="/my-account/profile">View My Account</a>
            </div>
        );
    }

    if (!activeChoice) {
        return <p role="status" className="section text-center">Registration is currently unavailable.</p>;
    }

    const unavailableReason = registrationFormUnavailableReason(activeChoice.form);

    const selectChoice = choice => {
        setActiveChoiceKey(choice.choiceKey);

        if (isBuilderPreview || !browserState) return;
        const nextUrl = buildRegistrationChoiceUrl(browserState, choice.linkKey);
        if (!nextUrl) return;

        window.history.replaceState(window.history.state, '', nextUrl);
        const parsedUrl = new URL(nextUrl, browserState.origin);
        setBrowserState(previous => ({
            ...previous,
            pathname: parsedUrl.pathname,
            search: parsedUrl.search,
            hash: parsedUrl.hash
        }));
    };

    return (
        <AuthFormShell {...props} defaultWidth="wide">
        <section aria-label="Registration options">
            <h2 className={classNames(
                'text-center',
                props.values?.introText ? '!mb-4' : '!mb-6'
            )}>{props.values?.heading || 'Register'}</h2>
            {props.values?.introText ? (
                <div
                    className="rich-text text-center mb-6"
                    dangerouslySetInnerHTML={{__html: props.values.introText}}
                />
            ) : null}
            {choices.length > 1 ? (
                <RadioGroup
                    value={activeChoice.choiceKey}
                    onChange={choiceKey => selectChoice(choices.find(choice => choice.choiceKey === choiceKey))}
                    className="mb-5"
                >
                    <RadioGroup.Label className="sr-only">Choose how to register</RadioGroup.Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {choices.map(choice => (
                            <RadioGroup.Option
                                key={choice.choiceKey}
                                value={choice.choiceKey}
                                className={({checked}) => classNames(
                                    'relative block rounded-theme border bg-white shadow-sm px-3 py-2 text-left ' +
                                    'cursor-pointer transition-colors duration-150 motion-reduce:transition-none focus:outline-none',
                                    checked
                                        ? 'border-base bg-light text-colour-base'
                                        : 'border-gray-300 hover:border-gray-400'
                                )}
                            >
                                {({checked}) => (
                                    <>
                                        <RadioGroup.Label
                                            as="span"
                                            className={classNames(
                                                'block text-sm font-medium',
                                                checked ? 'text-colour-base' : 'text-gray-700'
                                            )}
                                        >
                                            {choice.label || 'Registration'}
                                        </RadioGroup.Label>
                                        {choice.description ? (
                                            <RadioGroup.Description
                                                as="span"
                                                className={classNames(
                                                    'block text-xs leading-4 mt-0.5',
                                                    checked ? 'text-colour-base' : 'text-gray-500'
                                                )}
                                            >
                                                {choice.description}
                                            </RadioGroup.Description>
                                        ) : null}
                                    </>
                                )}
                            </RadioGroup.Option>
                        ))}
                    </div>
                </RadioGroup>
            ) : null}

            <ContentTransition transitionKey={activeChoice.choiceKey} duration={200} offset={0}>
                <div key={activeChoice.choiceKey}>
                    {!unavailableReason ? (
                        <RegistrationForm
                            from={returnUrl}
                            node={node}
                            preview={isBuilderPreview}
                            values={{form: activeChoice.form}}
                        />
                    ) : (
                        <p role="status" className="text-center">{unavailableReason}</p>
                    )}
                </div>
            </ContentTransition>
        </section>
        </AuthFormShell>
    );
}
